import questionDictionary from '@/data/moduli_questions_dictionary_COMPREHENSIVE.json';

type ModuleDictionaryEntry = {
  module?: string;
  type?: string;
  dictionary?: Record<string, string>;
};

const dictionaryEntries = questionDictionary as ModuleDictionaryEntry[];

export const stripDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const normalizeKey = (value: string): string =>
  stripDiacritics(String(value || '').trim().toLowerCase());

const moduleKeyVariants = (value: string): string[] => {
  const base = normalizeKey(value);
  const stripped = base.replace(/^\d+\)?\s*/, '').replace(/^\d+\s*/, '');
  return Array.from(new Set([base, stripped].filter(Boolean)));
};

const buildDictionaryMap = () => {
  const map = new Map<
    string,
    { raw: Record<string, string>; normalized: Map<string, string> }
  >();

  dictionaryEntries.forEach((entry) => {
    const raw = entry.dictionary || {};
    const normalized = new Map<string, string>();
    Object.entries(raw).forEach(([key, value]) => {
      normalized.set(normalizeKey(key), value);
    });

    const candidates = [entry.type, entry.module].filter(Boolean) as string[];
    candidates.forEach((candidate) => {
      moduleKeyVariants(candidate).forEach((variant) => {
        if (!variant) return;
        map.set(variant, { raw, normalized });
      });
    });
  });

  return map;
};

const moduleDictionaryMap = buildDictionaryMap();

export const normalizeValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '—';
  if (typeof raw === 'boolean') return raw ? 'Si' : 'No';

  if (Array.isArray(raw)) {
    const items = raw.map((item) => normalizeValue(item)).filter((item) => item !== '—');
    return items.length ? items.join(', ') : '—';
  }

  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if ('value' in obj) return normalizeValue(obj.value);
    try {
      return JSON.stringify(obj);
    } catch (error) {
      return String(obj);
    }
  }

  const text = String(raw).trim();
  if (!text) return '—';

  const normalized = normalizeKey(text);
  if (normalized === 'si' || normalized === 's' || normalized === 'yes') return 'Si';
  if (normalized === 'no' || normalized === 'n') return 'No';

  return text;
};

export const getReadableLabel = (
  moduleType: string | undefined,
  rawLabel: string
): string => {
  const label = String(rawLabel || '').trim();
  if (!label) return '—';

  if (moduleType) {
    const moduleKeys = moduleKeyVariants(moduleType);
    for (const key of moduleKeys) {
      const dictionary = moduleDictionaryMap.get(key);
      if (!dictionary) continue;
      if (dictionary.raw[label]) return dictionary.raw[label];
      const normalized = dictionary.normalized.get(normalizeKey(label));
      if (normalized) return normalized;
    }
  }

  if (/^\d+(\.\d+)+$/.test(label)) return `Campo ${label}`;
  return label;
};

export const formatDate = (raw?: string | number | Date): string => {
  if (!raw) return '';
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('it-IT');
};

export const getFieldValue = (
  content: Record<string, unknown> | undefined,
  key: string
): string => {
  if (!content) return '';
  const target = normalizeKey(key);
  for (const [k, v] of Object.entries(content)) {
    if (normalizeKey(k) !== target) continue;
    if (v === null || v === undefined) return '';
    if (typeof v === 'object' && v !== null && 'value' in (v as Record<string, unknown>)) {
      return String((v as Record<string, unknown>).value ?? '').trim();
    }
    return String(v ?? '').trim();
  }
  return '';
};

export const findCompanyInfoModule = (
  modules: Array<{ name?: string; type?: string }> | undefined
): { name?: string; type?: string } | undefined => {
  const target = normalizeKey('Dati info azienda');
  return modules?.find((module) =>
    normalizeKey(module?.type || module?.name || '') === target
  );
};
